import React, { useEffect, useState } from "react";
import Breadcrumb from "../../../CommonComponents/BreadCrumb/BreadCrumb";
import CustomTable from "../../../CommonComponents/Table/CustomTable";
import AdminPagination from "../../../CommonComponents/Pagination/AdminPagination";
import { networkService } from "../../../config/axios";
import "../admin.css";
import "./adminApiUsage.css";

const PER_PAGE = 50;

const formatCost = (value) => `$${Number(value || 0).toFixed(4)}`;
const formatNumber = (value) => Number(value || 0).toLocaleString();

// This page is the super-admin-wide view over ApiCallLog. Once calls are
// attributed to the user whose own Claude API key was used, a "My API Usage"
// page can reuse the same ApiCallLog.totals_for model method and a
// differently-scoped controller action — this component's summary/table/filter
// shape is intentionally generic (data-driven, not admin-specific) so it can
// be reused there too.
const AdminApiUsagePage = () => {
    const [logs, setLogs] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [taskTypes, setTaskTypes] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [totals, setTotals] = useState({ total_cost_usd: 0, total_input_tokens: 0, total_output_tokens: 0, total_calls: 0 });
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [clientId, setClientId] = useState("");
    const [taskType, setTaskType] = useState("");
    const [loading, setLoading] = useState(true);

    const isSuperAdmin = !!currentUser?.super_admin;

    const fetchLogs = async () => {
        setLoading(true);
        const params = new URLSearchParams({ page, per_page: PER_PAGE });
        if (clientId) params.set("client_id", clientId);
        if (taskType) params.set("task_type", taskType);

        const response = await networkService(`admin/api_call_logs.json?${params.toString()}`, "GET");
        setLogs(response.data?.api_call_logs || []);
        setTotal(response.data?.total || 0);
        setTotals(response.data?.totals || {});
        setTaskTypes(response.data?.task_types || []);
        setCurrentUser(response.data?.current_user || null);
        setLoading(false);
    };

    useEffect(() => {
        fetchLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, clientId, taskType]);

    useEffect(() => {
        if (!isSuperAdmin) return;
        networkService("admin/orgs.json", "GET").then((response) => {
            setCompanies(response.data || []);
        });
    }, [isSuperAdmin]);

    const columns = React.useMemo(() => [
        {
            Header: "Date",
            accessor: "timestamp",
            Cell: ({ value }) => value ? new Date(value).toLocaleString() : "—",
        },
        {
            Header: "Company",
            accessor: "company_name",
            Cell: ({ value }) => value || "—",
        },
        {
            Header: "Task",
            accessor: "task_type",
        },
        {
            Header: "Model",
            accessor: "model_used",
        },
        {
            Header: "Input Tokens",
            accessor: "input_tokens",
            Cell: ({ value }) => formatNumber(value),
        },
        {
            Header: "Output Tokens",
            accessor: "output_tokens",
            Cell: ({ value }) => formatNumber(value),
        },
        {
            Header: "Cost",
            accessor: "cost_usd",
            Cell: ({ value }) => formatCost(value),
        },
    ], []);

    return (
        <div className="admin-module">
            <div className="admin-api-usage-page admin-content">
                <Breadcrumb string="Admin / API Usage" />

                <div className="container admin-api-usage-container">
                    <div className="admin-api-usage-summary">
                        <div className="admin-api-usage-card">
                            <span className="admin-api-usage-card-label">Total Cost</span>
                            <span className="admin-api-usage-card-value">{formatCost(totals.total_cost_usd)}</span>
                        </div>
                        <div className="admin-api-usage-card">
                            <span className="admin-api-usage-card-label">Total Calls</span>
                            <span className="admin-api-usage-card-value">{formatNumber(totals.total_calls)}</span>
                        </div>
                        <div className="admin-api-usage-card">
                            <span className="admin-api-usage-card-label">Input Tokens</span>
                            <span className="admin-api-usage-card-value">{formatNumber(totals.total_input_tokens)}</span>
                        </div>
                        <div className="admin-api-usage-card">
                            <span className="admin-api-usage-card-label">Output Tokens</span>
                            <span className="admin-api-usage-card-value">{formatNumber(totals.total_output_tokens)}</span>
                        </div>
                    </div>

                    <div className="admin-api-usage-filters">
                        {isSuperAdmin ? (
                            <select
                                className="admin-api-usage-filter-select"
                                value={clientId}
                                onChange={(e) => { setPage(1); setClientId(e.target.value); }}
                            >
                                <option value="">All companies</option>
                                {companies?.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        ) : (
                            currentUser?.company_name && (
                                <span className="admin-api-usage-company-label">{currentUser.company_name}</span>
                            )
                        )}

                        <select
                            className="admin-api-usage-filter-select"
                            value={taskType}
                            onChange={(e) => { setPage(1); setTaskType(e.target.value); }}
                        >
                            <option value="">All task types</option>
                            {taskTypes?.map((t) => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>

                    <div className="top-parent admin-api-usage-table-wrapper">
                        {loading ? (
                            <p className="admin-api-usage-loading">Loading…</p>
                        ) : (
                            <CustomTable data={logs} columnData={columns} />
                        )}
                    </div>

                    {total > PER_PAGE && (
                        <AdminPagination
                            dataPerPage={PER_PAGE}
                            totalData={total}
                            currentPage={page}
                            onPageChange={setPage}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminApiUsagePage;
